// Training statistics from 1 Hz samples.
// samples = { t: [], p: [], c: [], hr: [], v: [], d: [], alt: [] }  (power W, cadence rpm, HR bpm, speed m/s, distance m, altitude m)
import { ZONES } from './workouts.js';
import { startOfDay, startOfWeek, addDays } from './culture.js';

/** 1 Hz samples of an activity. */
export interface Samples { t: number[]; p: number[]; c: number[]; hr: number[]; v: number[]; d: number[]; alt: number[] }
/**
 * Longest activity the server accepts, in 1 Hz samples: a day. A ride is sent to the server as it goes (ADR 0019), so
 * the bound is about the size of one stored activity (about 8 MB of JSON), not about what a browser holds at the end.
 * contracts/MAX_ACTIVITY_SAMPLES states the same figure for the schemas; apps/api/test checks they agree.
 */
export const MAX_ACTIVITY_S = 24 * 3600;
export interface Summary {
  duration: number; distance: number; avgPower: number; maxPower: number; np: number; IF: number; TSS: number; kJ: number;
  avgHR: number; maxHR: number; avgCad: number; avgSpeed: number; maxSpeed: number; elevation: number; zones: number[];
  bests: Record<number, number>; wkg: number;
}
/** What the aggregates need from an activity. */
export interface ActivityLike { date: number; summary: Summary }


export const EFFORT_DURATIONS = [5, 15, 30, 60, 120, 300, 600, 1200, 3600];
export const effortLabel = (d: number): string => (d < 60 ? `${d} s` : d < 3600 ? `${d / 60} min` : `${d / 3600} h`);

/** Best rolling average over `dur` seconds. */
export function bestEffort(p: number[], dur: number): number {
  if (!p || p.length < dur) return 0;
  let s = 0;
  for (let i = 0; i < dur; i++) s += p[i];
  let best = s;
  for (let i = dur; i < p.length; i++) { s += p[i] - p[i - dur]; if (s > best) best = s; }
  return Math.round(best / dur);
}

/** Normalized power (30 s rolling average to the 4th power). */
export function normalizedPower(p: number[]): number {
  if (!p || p.length < 30) return p && p.length ? Math.round(p.reduce((a, b) => a + b, 0) / p.length) : 0;
  let s = 0, acc = 0, n = 0;
  for (let i = 0; i < p.length; i++) {
    s += p[i]; if (i >= 30) s -= p[i - 30];
    if (i >= 29) { acc += (s / 30) ** 4; n++; }
  }
  return Math.round((acc / n) ** 0.25);
}

const avg = (a: number[]): number => (a && a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
const nz = (a: number[] | undefined): number[] => (a || []).filter(x => x > 0);

export function summarize(S: Samples, ftp = 250, mass = 75): Summary {
  const n = S.p.length, dur = n ? S.t[n - 1] - S.t[0] + 1 : 0;
  const np = normalizedPower(S.p), IF = ftp ? np / ftp : 0;
  const zones = new Array(ZONES.length).fill(0);
  for (const w of S.p) { const f = w / ftp; zones[ZONES.findIndex(z => f < z.max)]++; }
  let gain = 0;
  for (let i = 1; i < (S.alt || []).length; i++) gain += Math.max(0, S.alt[i] - S.alt[i - 1]);
  const bests: Record<number, number> = {};
  for (const d of EFFORT_DURATIONS) if (n >= d) bests[d] = bestEffort(S.p, d);
  return {
    duration: dur, distance: n ? S.d[n - 1] : 0,
    avgPower: Math.round(avg(S.p)), maxPower: n ? Math.max(...S.p) : 0, np, IF: +IF.toFixed(2),
    TSS: Math.round((dur / 3600) * IF * IF * 100), kJ: Math.round(S.p.reduce((a, b) => a + b, 0) / 1000),
    avgHR: Math.round(avg(nz(S.hr))), maxHR: Math.max(0, ...(S.hr || [])), avgCad: Math.round(avg(nz(S.c))),
    avgSpeed: dur ? (n ? S.d[n - 1] : 0) / dur : 0, maxSpeed: n ? Math.max(...S.v) : 0,
    elevation: Math.round(gain), zones, bests, wkg: +(avg(S.p) / mass).toFixed(2),
  };
}

/** Start of the calendar day, in time zone `tz` (IANA) or the runtime's if absent. */
export const dayKey = (ts: number, tz?: string): number => startOfDay(ts, tz);

/** Fitness (CTL, 42 d), fatigue (ATL, 7 d) and freshness (TSB) day by day. */
export interface FitnessDay { day: number; tss: number; ctl: number; atl: number; tsb: number }
export function fitnessSeries(activities: ActivityLike[], days = 90, now = Date.now(), tz?: string): FitnessDay[] {
  const tssByDay = new Map<number, number>();
  for (const a of activities) { const k = dayKey(a.date, tz); tssByDay.set(k, (tssByDay.get(k) || 0) + (a.summary?.TSS || 0)); }
  const end = dayKey(now, tz), first = activities.length ? Math.min(...activities.map(a => dayKey(a.date, tz))) : end;
  const start = Math.min(first, addDays(end, -days, tz));
  let ctl = 0, atl = 0;
  const out: FitnessDay[] = [];
  for (let d = start; d <= end; d = addDays(d, 1, tz)) {
    const tss = tssByDay.get(d) || 0;
    const tsb = ctl - atl;
    ctl += (tss - ctl) / 42; atl += (tss - atl) / 7;
    if (d > addDays(end, -days, tz)) out.push({ day: d, tss, ctl: +ctl.toFixed(1), atl: +atl.toFixed(1), tsb: +tsb.toFixed(1) });
  }
  return out;
}

/** Power curve: best effort per duration across a set of activities. */
export function powerCurve(activities: ActivityLike[], since = 0): Record<number, number> {
  const out: Record<number, number> = {};
  for (const d of EFFORT_DURATIONS) out[d] = 0;
  for (const a of activities) if (a.date >= since) for (const d of EFFORT_DURATIONS) out[d] = Math.max(out[d], a.summary?.bests?.[d] || 0);
  return out;
}

/** Totals per week (Monday) over the last n weeks. */
export interface WeekTotals { week: number; time: number; dist: number; tss: number; rides: number; kJ: number }
export function weekly(activities: ActivityLike[], weeks = 8, now = Date.now(), tz?: string): WeekTotals[] {
  const monday = (ts: number) => startOfWeek(ts, tz);
  const cur = monday(now), out: WeekTotals[] = [];
  for (let i = weeks - 1; i >= 0; i--) out.push({ week: addDays(cur, -7 * i, tz), time: 0, dist: 0, tss: 0, rides: 0, kJ: 0 });
  for (const a of activities) {
    const w = out.find(o => o.week === monday(a.date));
    if (!w) continue;
    w.time += a.summary.duration; w.dist += a.summary.distance; w.tss += a.summary.TSS; w.rides++; w.kJ += a.summary.kJ;
  }
  return out;
}

/** Number of consecutive days with at least one activity, ending today or yesterday. */
export function streak(activities: { date: number }[], now = Date.now(), tz?: string): number {
  const days = new Set(activities.map(a => dayKey(a.date, tz)));
  let d = dayKey(now, tz);
  if (!days.has(d)) d = addDays(d, -1, tz);
  let n = 0;
  while (days.has(d)) { n++; d = addDays(d, -1, tz); }
  return n;
}
